import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { MousePointerClick, FileCheck, Monitor, Users, FileText, Compass, FolderCheck } from "lucide-react";

const STEPS = [
  { icon: FileCheck, title: "Candidatura telematica", desc: "Dati anagrafici, documenti e preferenze di corso: tutto online, in pochi minuti." },
  { icon: Monitor, title: "Test proctored", desc: "Prova scritta a risposta chiusa e mappa di allineamento al percorso, in sessione controllata da remoto." },
  { icon: Users, title: "Colloquio motivazionale", desc: "L'ultimo passaggio: un colloquio in presenza per completare il percorso di selezione." },
];

const CTA_FEATURES = [
  { icon: FileText, label: "Candidature digitali", testid: "cta-feature-candidature" },
  { icon: Compass, label: "Mappa di allineamento", testid: "cta-feature-test" },
  { icon: FolderCheck, label: "Documenti automatici", testid: "cta-feature-documenti" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="max-w-3xl fade-up">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Il tuo futuro tecnico inizia con un <span className="text-[#003366]">ITS</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
            ITS Match è la piattaforma che gli Istituti Tecnici Superiori utilizzano per gestire la selezione dei
            candidati ai propri percorsi post-diploma: candidatura telematica con caricamento dei documenti, prova
            scritta online in modalità proctored, la rotta ITS — mappa di allineamento al percorso — e il colloquio motivazionale.
          </p>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            Per candidarti, visita la pagina dedicata dell'istituto di tuo interesse: ogni ITS pubblica il proprio
            bando di selezione con i corsi attivi e le modalità di ammissione.
          </p>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="eyebrow mb-3">Come funziona</p>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Il percorso di selezione</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <div key={s.title} data-testid={`landing-step-${i}`} className="institutional-card fade-up rounded-xl border border-slate-200 bg-white p-6" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="h-10 w-10 rounded-lg bg-[#E0E7FF] text-[#003366] flex items-center justify-center mb-4">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-normal">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex items-center gap-3 text-sm text-slate-500">
            <MousePointerClick className="h-4 w-4 text-[#0284C7]" />
            Le candidature si presentano dalla pagina dedicata di ciascun istituto.
          </div>
        </div>
      </section>

      <section data-testid="cta-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="max-w-3xl mx-auto text-center fade-up">
          <p className="eyebrow mb-3" data-testid="cta-label">IL PROSSIMO PASSO</p>
          <h2 data-testid="cta-title" className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Attiva <span className="text-[#003366]">ITS Match</span>: più candidati, scelti meglio.
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            {CTA_FEATURES.map((f) => (
              <div key={f.label} data-testid={f.testid} className="flex items-center gap-2.5">
                <div className="h-10 w-10 rounded-lg bg-[#E0E7FF] text-[#003366] flex items-center justify-center">
                  <f.icon className="h-5 w-5" />
                </div>
                <span className="text-sm font-semibold text-slate-800">{f.label}</span>
              </div>
            ))}
          </div>
          <p data-testid="cta-description" className="mt-8 text-base sm:text-lg text-slate-600 leading-relaxed">
            Attivazione e utilizzo gratuiti fino al 31 dicembre, poi canone mensile. Configuriamo insieme la tua
            prima selezione.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button data-testid="cta-email-button" asChild size="lg" className="bg-[#003366] hover:bg-[#002244] rounded-full px-8">
              <a href="mailto:info@itsmatch.it">Attivalo gratis ora</a>
            </Button>
            <a data-testid="cta-email-text" href="mailto:info@itsmatch.it" className="font-bold text-[#003366] hover:underline">
              info@itsmatch.it
            </a>
          </div>
        </div>
      </section>

      <footer className="bg-[#003366] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="font-display font-bold">ITS Match · Selezione ITS Academy</div>
          <p className="text-sm text-blue-200">La piattaforma per l'ammissione ai percorsi degli Istituti Tecnici Superiori</p>
        </div>
      </footer>
    </div>
  );
}
